import clsx from "clsx";
import type { TableColumn } from "../../DataTable/DataTable";

export type CardActions = {
  onEdit?: () => void;
  onDelete?: () => void;
};

export type CardGridColumns = 1 | 2 | 3 | 4;

const gridColsClass: Record<CardGridColumns, string> = {
  1: "grid-cols-1",
  2: "grid-cols-1 md:grid-cols-2",
  3: "grid-cols-1 md:grid-cols-2 xl:grid-cols-3",
  4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
};

export type CrudCardsProps<T extends Record<string, any>> = {
  data: T[];
  columns: TableColumn<T>[];
  onRowClick?: (id: any) => void;
  onEdit?: (id: any) => void;
  onDelete?: (id: any) => Promise<any>;
  isFetching?: boolean;
  emptyMessage?: string;
  renderCard?: (item: T, actions: CardActions) => React.ReactNode;
  gridColumns?: CardGridColumns;
};

function DefaultCardContent<T extends Record<string, any>>({
  item,
  columns,
}: {
  item: T;
  columns: TableColumn<T>[];
}) {
  const visibleColumns = columns.filter((col) => !col.isActionColumn);
  const [titleCol, ...otherCols] = visibleColumns;

  return (
    <div className="p-4 flex flex-col gap-2 flex-1">
      {titleCol && (
        <div className="font-semibold text-slate-700 text-sm">
          {titleCol.customRender
            ? titleCol.customRender(item)
            : String(item[titleCol.field as keyof T] ?? "-")}
        </div>
      )}
      {otherCols.map((col) => (
        <div key={String(col.field)} className="flex flex-col gap-0.5">
          <span className="text-xs text-slate-400">{col.header}</span>
          <span className="text-xs text-slate-600">
            {col.customRender
              ? col.customRender(item)
              : String(item[col.field as keyof T] ?? "-")}
          </span>
        </div>
      ))}
    </div>
  );
}

function CrudCards<T extends Record<string, any>>({
  data,
  columns,
  onRowClick,
  onEdit,
  onDelete,
  isFetching,
  emptyMessage,
  renderCard,
  gridColumns = 3,
}: CrudCardsProps<T>) {
  const gridClass = gridColsClass[gridColumns];

  if (isFetching && !data?.length) {
    return (
      <div className={`grid ${gridClass} gap-4 p-4`}>
        {["s1", "s2", "s3", "s4", "s5", "s6"].map((key) => (
          <div
            key={key}
            className="border border-slate-200 rounded-lg bg-white h-32 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className="flex items-center justify-center p-8 text-slate-400 text-sm italic">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={`grid ${gridClass} gap-4 p-4`}>
      {data.map((item) => (
        <div
          key={item.id}
          className={clsx(
            "border border-slate-200 rounded-lg bg-white flex flex-col overflow-hidden",
            (onRowClick || onEdit) &&
              "cursor-pointer hover:shadow-md hover:border-primary-300 transition-all"
          )}
          onClick={() => (onRowClick ?? onEdit)?.(item.id)}
        >
          {renderCard ? (
            renderCard(item, {
              onEdit: onEdit ? () => onEdit(item.id) : undefined,
              onDelete: onDelete ? () => onDelete(item.id) : undefined,
            })
          ) : (
            <DefaultCardContent<T> item={item} columns={columns} />
          )}
        </div>
      ))}
    </div>
  );
}

export default CrudCards;
